
import React from 'react';
import { SimulationConfig, MelodyInstrument } from '../../../types';
import { ControlGroup } from '../../../ControlGroup';
import { Toggle } from '../../../Toggle';
import { SmartInput } from '../../../SmartInput';

import { motion } from 'motion/react';

interface AudioOverridesProps {
    config: SimulationConfig;
    setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
    instruments: MelodyInstrument[];
}

export const AudioOverrides: React.FC<AudioOverridesProps> = ({ config, setConfig, instruments }) => {
    return (
        <div className={`flex flex-col gap-4 p-5 rounded-3xl border ${config.darkMode ? 'bg-black/20 border-white/5' : 'bg-white border-neutral-100 shadow-sm'} mb-4`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ControlGroup label="Bypass Stereo Echo" settingKey="universalNoStereoEcho" darkMode={config.darkMode}>
                    <Toggle active={config.universalNoStereoEcho} onClick={() => setConfig(p => ({...p, universalNoStereoEcho: !p.universalNoStereoEcho}))} color="bg-blue-600" />
                </ControlGroup>
                <ControlGroup label="Bypass Global Reverb" settingKey="universalNoReverb" darkMode={config.darkMode}>
                    <Toggle active={config.universalNoReverb} onClick={() => setConfig(p => ({...p, universalNoReverb: !p.universalNoReverb}))} color="bg-blue-600" />
                </ControlGroup>
                <ControlGroup label="Bypass Distortion" settingKey="universalNoDistortion" darkMode={config.darkMode}>
                    <Toggle active={config.universalNoDistortion} onClick={() => setConfig(p => ({...p, universalNoDistortion: !p.universalNoDistortion}))} color="bg-blue-600" />
                </ControlGroup>
                <ControlGroup label="Force Universal Voice" settingKey="universalForceInstrument" darkMode={config.darkMode}>
                    <div className={`flex items-center justify-end p-2.5 rounded-2xl border ${config.darkMode ? 'bg-black/20 border-white/5 shadow-inner' : 'bg-neutral-50 border-neutral-100 shadow-inner'}`}>
                        <select 
                            value={(config.universalForceInstrument as any)} 
                            onChange={e => setConfig(p => ({...p, universalForceInstrument: e.target.value as any}))} 
                            className={`w-full px-2 py-1.5 rounded-xl text-[10px] font-black outline-none cursor-pointer ${config.darkMode ? 'bg-transparent text-white' : 'bg-transparent text-neutral-800'}`}
                        >
                            <option value="NONE" className="bg-neutral-900 text-white">None (Factory Preset)</option>
                            {instruments.map(inst => <option key={inst} value={inst} className="bg-neutral-900 text-white">{inst.replace(/_/g, ' ')}</option>)}
                        </select>
                    </div>
                </ControlGroup>
            </div>

            <div className="h-px bg-white/5 w-full" />

            <SmartInput label="Master Pitch Offset" settingKey="universalMasterPitchShift" min={-24} max={24} step={1} value={config.universalMasterPitchShift} onChange={v => setConfig(p => ({...p, universalMasterPitchShift: v}))} darkMode={config.darkMode} />
            
            <ControlGroup label="Hardware Filter Override" settingKey="universalLowPassOverride" darkMode={config.darkMode}>
                <Toggle active={config.universalLowPassOverride} onClick={() => setConfig(p => ({...p, universalLowPassOverride: !p.universalLowPassOverride}))} color="bg-emerald-600" />
            </ControlGroup>
            
            {config.universalLowPassOverride && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}>
                    <SmartInput label="Master Cutoff Hz" settingKey="universalLowPassFreq" min={200} max={20000} step={100} value={config.universalLowPassFreq} onChange={v => setConfig(p => ({...p, universalLowPassFreq: v}))} darkMode={config.darkMode} />
                </motion.div>
            )}
        </div>
    );
};
